import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { bind, CardItem, StatusCard } from 'Mri.Web.Common';
import * as React from 'react';
import { Collapse } from 'reactstrap';

export interface IDisplayPowerCardProps {
    readonly statusPoll?: Mri.DisplayStatuses.DisplayStatusPoll;
    readonly telemetryPoll?: Mri.TelemetryPolls.TelemetryPoll;
    readonly loading?: boolean;
}

@observer
export class DisplayPowerCard extends React.Component<IDisplayPowerCardProps> {
    public render() {
        const statusPoll = this.props.statusPoll;
        const collapsible =
            statusPoll?.power?.powerSupplies?.length ||
            statusPoll?.power?.aux12VPowerSupplies?.length ||
            statusPoll?.power?.customPowerSupplies?.length;

        return (
            <StatusCard title="DC Power" loading={this.props.loading}>
                {statusPoll?.power && (
                    <>
                        <CardItem
                            label="Avg Voltage"
                            value={this.measurementValue(this.measurement(Mri.WebApi.MeasurementId.PrimaryPsuAvgVolts))}
                        />
                        <CardItem
                            label="Total Current"
                            value={this.measurementValue(
                                this.measurement(Mri.WebApi.MeasurementId.PrimaryPsuTotalAmps)
                            )}
                        />
                        <CardItem
                            label="Total Wattage"
                            value={this.measurementValue(
                                this.measurement(Mri.WebApi.MeasurementId.PrimaryPsuTotalWatts)
                            )}
                        />
                        <CardItem
                            label="Avg Temperature"
                            value={this.measurementValue(
                                this.measurement(Mri.WebApi.MeasurementId.PrimaryPsuAvgCelsius)
                            )}
                        />
                    </>
                )}
                {collapsible && (
                    <a className="btn btn-link p-0 p-md-2" onClick={this.onToggle}>
                        {this.collapsed ? 'Expand' : 'Hide'} Power Supplies
                    </a>
                )}
                <Collapse isOpen={!this.collapsed}>
                    {statusPoll?.power?.powerSupplies?.map((p, idx) => (
                        <PowerSupplyCardItemProps key={idx} powerSupply={p} powerSupplyId={idx + 1} />
                    ))}

                    {statusPoll?.power?.aux12VPowerSupplies?.map((p, idx) => (
                        <PowerSupplyCardItemProps
                            key={idx}
                            powerSupply={p}
                            powerSupplyId={idx + 1}
                            namePrefix="Aux 12V PowerSupply"
                        />
                    ))}

                    {statusPoll?.power?.customPowerSupplies?.map((p, idx) => (
                        <CustomPowerSupplyCardItemProps key={idx} powerSupply={p} powerSupplyId={idx + 1} />
                    ))}
                </Collapse>
            </StatusCard>
        );
    }

    @bind
    private measurement(measurementId: Mri.WebApi.MeasurementId) {
        if (this.props.telemetryPoll?.measurements) {
            return this.props.telemetryPoll.measurements.find(s => s.id == measurementId);
        }
    }

    @bind
    private measurementValue(measurement?: Mri.WebApi.Measurement) {
        if (measurement) {
            const units = measurement.units || '';

            if (measurement.boolValue) return measurement.boolValue + units;
            if (measurement.doubleValue) return measurement.doubleValue.toFixed(2) + units;
            if (measurement.floatValue) return measurement.floatValue.toFixed(2) + units;
            if (measurement.intValue) return measurement.intValue.toFixed(2) + units;
            if (measurement.stringValue) return measurement.stringValue + units;
            if (measurement.durationValue) return measurement.durationValue.toString() + units;
        } else return '-';
    }

    @bind
    @action
    private onToggle() {
        this.collapsed = !this.collapsed;
    }

    @observable
    private collapsed = true;
}

export interface IPowerSupplyCardItemProps {
    readonly powerSupply: Mri.WebApi.PowerSupply;
    readonly powerSupplyId: number;
    readonly namePrefix?: string;
}

@observer
export class PowerSupplyCardItemProps extends React.Component<IPowerSupplyCardItemProps> {
    public render() {
        const { powerSupply, powerSupplyId, namePrefix } = this.props;
        const name = powerSupply.name
            ? powerSupply.name
            : namePrefix
                ? `${namePrefix} ${powerSupplyId}`
                : `Power Supply ${powerSupplyId}`;

        return (
            <>
                <CardItem label={name} value="" header />
                <CardItem label="Voltage" value={powerSupply.voltage?.toFixed(2)} />

                <CardItem label="Current" value={powerSupply.current?.toFixed(2)} />
                <CardItem label="Wattage" value={this.wattage?.toFixed(2)} />

                <CardItem label="Temperature" value={powerSupply.temperature} />
            </>
        );
    }

    @computed
    private get wattage() {
        const { powerSupply } = this.props;

        if (powerSupply.wattage) {
            return powerSupply.wattage;
        } else if (powerSupply.current && powerSupply.voltage) {
            return powerSupply.current * powerSupply.voltage;
        }
    }
}

export interface ICustomPowerSupplyCardItemProps {
    readonly powerSupply: Mri.WebApi.CustomPowerSupply;
    readonly powerSupplyId: number;
}

@observer
export class CustomPowerSupplyCardItemProps extends React.Component<ICustomPowerSupplyCardItemProps> {
    public render() {
        const { powerSupply, powerSupplyId } = this.props;
        const name = powerSupply.name ?? `Custom Power Supply ${powerSupplyId}`;

        return (
            <>
                <CardItem label={name} value="" header />

                {powerSupply.voltages?.map((v, idx) => (
                    <CardItem key={idx} label={`Voltage ${idx + 1}`} value={v?.toFixed(2)} />
                ))}

                {powerSupply.currents?.map((c, idx) => (
                    <CardItem key={idx} label={`Current ${idx + 1}`} value={c?.toFixed(2)} />
                ))}

                {powerSupply.wattages?.map((w, idx) => (
                    <CardItem key={idx} label={`Wattage ${idx + 1}`} value={w?.toFixed(2)} />
                ))}
            </>
        );
    }
}
